import { FC, useContext, useEffect, useState, useMemo } from 'react';
import { Modal, Box, Typography, Button, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { LoadingWrapper } from '@components';
import { ToastMessageContext } from '@context';

import CloseMenu from '@assets/svg/closeMenu.svg?react';

// Import unified channel logo components
import { ChannelLogo } from '../ChannelLogos';

import { T_KEYS } from '../../constants';
import { ChannelType, ILinkedUnit } from '../../types';
import { useUnmapUnits } from '../../services/mapping.service';

// Styled Components
const ModalWrapper = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const ModalContainer = styled('div')({
  width: '689px',
  backgroundColor: 'rgba(255, 255, 255, 1)',
  color: '#261B6A',
  boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
  padding: '24px',
  borderRadius: '16px',
});

const ModalHeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

const ModalTitle = styled(Typography)({
  color: 'rgba(67, 83, 99, 1)',
  fontSize: '24px',
  fontWeight: 400,
  lineHeight: '28.8px',
  marginBottom: '24px',
});

const CloseButton = styled(CloseMenu)({
  cursor: 'pointer',
});

const ChannelsTitle = styled(Typography)({
  color: 'rgba(67, 83, 99, 1)',
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '25px',
  marginBottom: '16px',
});

const SelectContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '60px',
});

const SelectLeftContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

const SelectAvatar = styled(Avatar)({
  backgroundColor: 'lightBlue',
  width: 38,
  height: 38,
  marginLeft: '8px',
  marginRight: '8px',
});

const SelectChannelName = styled(Box)({
  textAlign: 'center',
  color: 'rgba(67, 83, 99, 1)',
  fontSize: '18px',
  lineHeight: '22px',
});

const SelectRightContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '54px',
});

const StatusLabelContainer = styled(Box)<{ isActive: boolean }>(
  ({ isActive }) => ({
    width: '70px',
    height: '25px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    backgroundColor: isActive ? '#F3FBF4' : '#FFFCF4',
    color: isActive ? '#16AE26' : '#FDBF21',
  })
);

const StatusDot = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: isActive ? '#16AE26' : '#FDBF21',
  marginRight: 0.5,
}));

const ActionButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive }) => ({
    width: '154px',
    height: '44px',
    borderRadius: '20px',
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'none',
    minWidth: '154px',
    minHeight: '44px',
    backgroundColor: isActive
      ? 'rgba(244, 67, 54, 0.1)'
      : 'rgba(156, 39, 176, 0.1)',
    color: isActive ? 'rgba(244, 67, 54, 1)' : 'rgba(156, 39, 176, 1)',
    '&:hover': {
      backgroundColor: isActive
        ? 'rgba(244, 67, 54, 0.1)'
        : 'rgba(156, 39, 176, 0.1)',
      color: isActive ? 'rgba(244, 67, 54, 1)' : 'rgba(156, 39, 176, 1)',
      opacity: 0.8,
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.38)',
    },
  })
);

const Divider = styled(Box)({
  height: '0.9px',
  width: '100%',
  backgroundColor: 'rgba(233, 233, 233, 1)',
  marginTop: 1,
  marginBottom: 3,
});

interface RenderSelectProps {
  disabled?: boolean;
  isAirbnb?: boolean;
  isBooking?: boolean;
  isActive?: boolean;
  onAction?: () => void;
}

const RenderSelect: FC<RenderSelectProps> = ({
  disabled = false,
  isAirbnb = false,
  isBooking = false,
  isActive = false,
  onAction = () => {},
}) => {
  const { t } = useTranslation();

  const getStatusLabel = () => {
    return isActive ? t(T_KEYS.STATUS_ACTIVE) : t(T_KEYS.STATUS_SUSPENDED);
  };

  const getChannelType = () => {
    if (isBooking) return 'booking';
    if (isAirbnb) return 'airbnb';
    return 'gathern';
  };

  return (
    <div>
      <SelectContainer>
        <SelectLeftContainer>
          <ChannelLogo channel={getChannelType()} size="md" rounded />
          <SelectChannelName>
            {isBooking ? 'Booking' : isAirbnb ? 'Airbnb' : t(T_KEYS.GATHERIN_CHANNEL)}
          </SelectChannelName>
        </SelectLeftContainer>
        <SelectRightContainer>
          <StatusLabelContainer isActive={isActive}>
            <StatusDot isActive={isActive} />
            {getStatusLabel()}
          </StatusLabelContainer>
          <ActionButton
            isActive={isActive}
            disabled={disabled}
            onClick={onAction}
          >
            {isActive ? t(T_KEYS.DEACTIVATE) : t(T_KEYS.ACTIVATE)}
          </ActionButton>
        </SelectRightContainer>
      </SelectContainer>
      <Divider />
    </div>
  );
};

export interface ChannelToggleModalProps {
  open: boolean;
  handleClose: () => void;
  propertyId: number;
  propertyName?: string;
  channels: ILinkedUnit[] | null;
}

const ChannelToggleModal: FC<ChannelToggleModalProps> = ({
  open,
  handleClose,
  propertyId,
  propertyName = '',
  channels,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useContext(ToastMessageContext);
  
  // Use the unmap API hook
  const { mutate: unmapUnits, isPending: isUnmapping } = useUnmapUnits();

  // Get channel info from channels array
  const channelInfo = useMemo(() => {
    const getChannel = (type: ChannelType) => channels?.find(c => c.channelType === type);
    
    return {
      airbnb: getChannel(ChannelType.Airbnb),
      booking: getChannel(ChannelType.Booking),
      gathern: getChannel(ChannelType.Gathern),
    };
  }, [channels]);
  
  // Check if any channel is active
  const hasActiveChannels = channelInfo.airbnb?.status || channelInfo.booking?.status || channelInfo.gathern?.status;

  // Track active state based on channel status
  const [airbnbActive, setAirbnbActive] = useState(false);
  const [gathernActive, setGathernActive] = useState(false);
  const [bookingActive, setBookingActive] = useState(false);

  useEffect(() => {
    setAirbnbActive(channelInfo.airbnb?.status === true);
    setGathernActive(channelInfo.gathern?.status === true);
    setBookingActive(channelInfo.booking?.status === true);
  }, [channelInfo]);

  const closeModal = () => {
    handleClose();
  };

  const handleChannelAction = async (channelType: 'airbnb' | 'booking' | 'gathern') => {
    const channel = channelInfo[channelType];
    
    if (!channel) {
      return showError(t(T_KEYS.HINT_SELECT));
    }

    const isCurrentlyActive = channelType === 'airbnb' ? airbnbActive 
      : channelType === 'booking' ? bookingActive 
      : gathernActive;

    if (isCurrentlyActive) {
      // Deactivate - call unmap API
      if (channel.userChannelId === null) {
        return showError(t(T_KEYS.SOMETHING_WENT_WRONG));
      }

      // Build the units array for unmap request
      const unitsToUnmap: { userChannelId: number; channelType: ChannelType; channelListingId: string | null }[] = [];

      // If deactivating Airbnb (base channel), unmap ALL active channels
      if (channelType === 'airbnb') {
        // Add Airbnb
        unitsToUnmap.push({
          userChannelId: channel.userChannelId,
          channelType: channel.channelType,
          channelListingId: channel.channelListingId,
        });
        
        // Add Booking if active
        if (channelInfo.booking?.status && channelInfo.booking.userChannelId !== null) {
          unitsToUnmap.push({
            userChannelId: channelInfo.booking.userChannelId,
            channelType: channelInfo.booking.channelType,
            channelListingId: channelInfo.booking.channelListingId,
          });
        }
        
        // Add Gathern if active
        if (channelInfo.gathern?.status && channelInfo.gathern.userChannelId !== null) {
          unitsToUnmap.push({
            userChannelId: channelInfo.gathern.userChannelId,
            channelType: channelInfo.gathern.channelType,
            channelListingId: channelInfo.gathern.channelListingId,
          });
        }
      } else {
        // For other channels, just unmap that single channel
        unitsToUnmap.push({
          userChannelId: channel.userChannelId,
          channelType: channel.channelType,
          channelListingId: channel.channelListingId,
        });
      }

      const unmapPayload = {
        propertyId,
        units: unitsToUnmap,
      };

      unmapUnits(unmapPayload, {
        onSuccess: () => {
          // Update local state
          if (channelType === 'airbnb') {
            // When Airbnb is deactivated, all channels are deactivated
            setAirbnbActive(false);
            setBookingActive(false);
            setGathernActive(false);
          } else if (channelType === 'booking') {
            setBookingActive(false);
          } else if (channelType === 'gathern') {
            setGathernActive(false);
          }
          showSuccess(t(T_KEYS.DE_ACTIVATE_SUCCESS));
          closeModal();
        },
        onError: (error: any) => {
          showError(error?.message || t(T_KEYS.SOMETHING_WENT_WRONG));
        },
      });
    } else {
      // Activate - API not implemented yet
      // TODO: Implement activate API when backend is ready
      showError(t(T_KEYS.SOMETHING_WENT_WRONG));
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="channel-toggle-modal"
      aria-describedby="channel-toggle-modal-description"
    >
      <ModalWrapper>
        <ModalContainer>
          <LoadingWrapper loading={isUnmapping}>
            <>
              <ModalHeaderContainer>
                <ModalTitle>
                  {t(T_KEYS.DE_ACTIVATE_CHANNEL)}
                </ModalTitle>
                <CloseButton onClick={closeModal} />
              </ModalHeaderContainer>
              <ChannelsTitle>
                {t(T_KEYS.CHANNELS)}
              </ChannelsTitle>
              {!hasActiveChannels && (
                <Typography sx={{ color: '#666', py: 2 }}>
                  {t(T_KEYS.NO_ACTIVE_CHANNELS)}
                </Typography>
              )}
              {channelInfo.airbnb?.status === true && (
                <RenderSelect
                  isAirbnb
                  isActive={airbnbActive}
                  onAction={() => handleChannelAction('airbnb')}
                />
              )}
              {channelInfo.booking?.status === true && (
                <RenderSelect
                  isBooking
                  isActive={bookingActive}
                  onAction={() => handleChannelAction('booking')}
                />
              )}
              {channelInfo.gathern?.status === true && (
                <RenderSelect
                  isActive={gathernActive}
                  onAction={() => handleChannelAction('gathern')}
                />
              )}
            </>
          </LoadingWrapper>
        </ModalContainer>
      </ModalWrapper>
    </Modal>
  );
};

export default ChannelToggleModal;
